import { hasPageBreak } from '@efdigital/ll-addon-content/utils/quiz-css-class';
import type { QuizQuestion } from '@efdigital/ll-addon-data';

import { MultipleChoiceQuestionManager } from './managers/choice-multiple.ts';
import { SingleChoiceQuestionManager } from './managers/choice-single.ts';
import { HiddenQuestionManager } from './managers/hidden.ts';
import { MetaQuestionManager } from './managers/meta.ts';
import { PageBreakQuestionManager } from './managers/page-break.ts';
import { PointsQuestionManager } from './managers/points.ts';
import { RankQuestionManager } from './managers/rank.ts';
import { TextQuestionManager } from './managers/text.ts';
import type { QuestionManager } from './types/question-manager.ts';

export const QUESTION_MANAGERS = [
  MetaQuestionManager,
  PageBreakQuestionManager,
  null,
  TextQuestionManager,
  SingleChoiceQuestionManager,
  MultipleChoiceQuestionManager,
  PointsQuestionManager,
  RankQuestionManager,
  null,
  HiddenQuestionManager,
];

export function lookupQuestionManager(question: QuizQuestion): QuestionManager {
  const matchingManager = QUESTION_MANAGERS.find((manager) =>
    manager?.discriminator(question)
  );
  if (!matchingManager) {
    console.error('Invalid question type', question);
    throw new Error(
      `No question manager found for question type: ${question.type}`
    );
  }

  return matchingManager;
}

export const eq = (a: number, b: number) => a === b;

export function addOne(n: number) {
  return n + 1;
}

/**
 * Calculate which page a question belongs to based on page-break classes.
 * @param questions - Array of all questions
 * @param currentIndex - Index of the question to check
 * @returns The page number (1-based)
 */
export function getPageNumber(
  questions: QuizQuestion[],
  currentIndex: number
): number {
  let pageNumber = 1;
  for (let i = 0; i <= currentIndex; i++) {
    if (i > 0 && hasPageBreak(questions[i]?.css || '')) {
      pageNumber++;
    }
  }
  return pageNumber;
}

/**
 * Check if any question in the quiz has a page break.
 * @param questions - Array of all questions
 * @returns True if at least one page break exists
 */
export function hasAnyPageBreaks(questions: QuizQuestion[]): boolean {
  return questions.some((q) => hasPageBreak(q.css || ''));
}

export type EditingMode =
  | 'inactive'
  | 'markdown'
  | 'question'
  | 'scoring'
  | 'response'
  | 'reporting';
